import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SafetyFunctionWithArchitecture } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  validateSafetyFunctionForm,
  type SafetyFunctionFormData,
} from './safetyFunctionForm.schema'
import { toSafetyFunctionFormData } from './safetyFunctionsUtils'
import {
  SISTEMA_PLR_DET_OPTIONS,
  SISTEMA_PLR_OPTIONS,
} from './sistemaFieldOptions'

const PLCAL_NOT_SET_VALUE = '__not_set__'

type SelectField = 'plcal' | 'plrdet'

type EditSafetyFunctionSelectFieldDialogProps = {
  isOpen: boolean
  onClose: () => void
  safetyFunction: SafetyFunctionWithArchitecture
  field: SelectField
  labelKey: string
  onSave: (data: SafetyFunctionFormData) => void
  isSaving: boolean
}

export function EditSafetyFunctionSelectFieldDialog({
  isOpen,
  onClose,
  safetyFunction,
  field,
  labelKey,
  onSave,
  isSaving,
}: EditSafetyFunctionSelectFieldDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [value, setValue] = useState('')

  const options =
    field === 'plcal' ? SISTEMA_PLR_OPTIONS : SISTEMA_PLR_DET_OPTIONS
  const enumPrefix = field === 'plcal' ? 'plr' : 'plrdet'

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const formData = toSafetyFunctionFormData(safetyFunction)
    setValue(formData[field])
  }, [field, isOpen, safetyFunction])

  const canSubmit = useMemo(() => {
    const mergedData: SafetyFunctionFormData = {
      ...toSafetyFunctionFormData(safetyFunction),
      [field]: value,
    }
    return validateSafetyFunctionForm(mergedData).success
  }, [field, safetyFunction, value])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmit = () => {
    if (!canSubmit || isSaving) {
      return
    }

    onSave({
      ...toSafetyFunctionFormData(safetyFunction),
      [field]: value,
    })
  }

  const fieldLabel = t(labelKey)
  const selectValue =
    field === 'plcal' && value.trim() === '' ? PLCAL_NOT_SET_VALUE : value
  const selectItems = useMemo(() => {
    const items: Array<{ value: string; label: string }> = []
    if (field === 'plcal') {
      items.push({
        value: PLCAL_NOT_SET_VALUE,
        label: t('safetyFunctionsPage.sfDetail.plcalNotSet'),
      })
    }
    for (const option of options) {
      items.push({
        value: option,
        label: t(`safetyFunctionsPage.enums.${enumPrefix}.${option}`),
      })
    }
    return items
  }, [enumPrefix, field, options, t])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {t('safetyFunctionsPage.sfDetail.editSelectFieldDialog.title', {
              field: fieldLabel,
            })}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label htmlFor={`sf-edit-${field}`}>{fieldLabel}</Label>
          <Select
            value={selectValue || undefined}
            onValueChange={(nextValue) => {
              if (!nextValue) {
                return
              }
              if (field === 'plcal' && nextValue === PLCAL_NOT_SET_VALUE) {
                setValue('')
                return
              }
              setValue(nextValue)
            }}
            items={selectItems}
          >
            <SelectTrigger id={`sf-edit-${field}`} className='w-full'>
              <SelectValue
                placeholder={t('safetyFunctionsPage.architecture.selectOption')}
              />
            </SelectTrigger>
            <SelectContent>
              {field === 'plcal' ? (
                <SelectItem value={PLCAL_NOT_SET_VALUE}>
                  {t('safetyFunctionsPage.sfDetail.plcalNotSet')}
                </SelectItem>
              ) : null}
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`safetyFunctionsPage.enums.${enumPrefix}.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field === 'plcal' ? (
            <p className='text-sm text-muted-foreground'>
              {t('safetyFunctionsPage.sfDetail.plcalHelper')}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSaving}>
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
